import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import type { UseQueryWrapper } from '../../../types/useQuery.types';

import type { GetReportDataFilterSubOwnersParams } from '../../../Domain/Reports/Services/ReportService';
import { reportService } from '../../../Domain/Reports/Services/ReportService';

import { useSnakeBar } from '../../useSnakeBar';
import type { ReportSubOwnersResponse } from '../../../types/report.types';

type GetReportDataFilterSubOwnersQueryResultData = ReportSubOwnersResponse;

type GetReportDataFilterSubOwnersQueryVariables = {
  [Key in keyof GetReportDataFilterSubOwnersParams]: GetReportDataFilterSubOwnersParams[Key] | undefined;
};

const queryFn =
  ({ userId, page, limit, searchQuery }: GetReportDataFilterSubOwnersQueryVariables) =>
  async () => {
    if (userId) {
      const response = await reportService.getReportDataFilterSubOwners({
        userId,
        page: page ?? 1,
        limit: limit ?? 25,
        searchQuery: searchQuery ?? undefined,
      });
      return (response as AxiosResponse).data;
    }
  };

const QUERY_KEY = 'getReportDataFilterSubOwners';

export const useReportsGetDataFilterSubOwnersQuery: UseQueryWrapper<
  GetReportDataFilterSubOwnersQueryResultData,
  GetReportDataFilterSubOwnersQueryVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  return useQuery<GetReportDataFilterSubOwnersQueryResultData>(
    [QUERY_KEY, variables.userId, variables.page, variables.limit, variables.searchQuery],
    queryFn(variables),
    {
      onError,
      keepPreviousData: true,
      ...(options || {}),
    },
  );
};
